import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../redux/store";
import { BoardRecordSetPublic } from "../../../services/venueService";
import {
    formatMetricValue,
    matchIdForMetricValue,
    metricLabel,
    recordForMetric,
    VenueMetric,
    VENUE_METRICS,
} from "../leaderboardModel";
import { VenuePersonalHistory } from "../venueHistory";
import { historyBest } from "../leaderboardModel";

const KEY = "leaderboards.VenuePage.";

export interface PersonalBestTilesProps {
    history: VenuePersonalHistory;
    /** All-time venue records, so a PB that *is* the record can say so. */
    allTime?: BoardRecordSetPublic | null;
    /** Opens the match that set a tile's personal best. */
    onOpenMatch: (matchId: string) => void;
}

/**
 * "Your personal bests here" — three tiles (SCORZA-739 design handoff, reference/02).
 *
 * The badge is the point of the section: a number is just a number until it says whether it is
 * merely your best or the best anyone has managed at this ground.
 */
export const PersonalBestTiles: FC<PersonalBestTilesProps> = ({
    history,
    allTime,
    onOpenMatch,
}) => {
    const { t } = useTranslation();
    const { useImperial } = useSelector(
        (root: RootState) => root.settings.settings
    );

    return (
        <div className="venue-pb-grid" data-testid="venue-personal-bests">
            {VENUE_METRICS.map((metric: VenueMetric) => {
                const best = historyBest(history, metric);
                const record = recordForMetric(allTime, metric);
                const formatted = formatMetricValue(
                    metric,
                    best,
                    useImperial,
                    t
                );

                let badgeKey = "pbBadgeYours";
                let badgeClass = "venue-pb-badge";
                if (!best) {
                    badgeKey = "pbBadgeNone";
                    badgeClass += " venue-pb-badge-none";
                } else if (record && best >= record.value) {
                    badgeKey = "pbBadgeVenueBest";
                    badgeClass += " venue-pb-badge-record";
                }

                const matchId = matchIdForMetricValue(
                    history.matches,
                    metric,
                    best
                );

                const tile = (
                    <>
                        <div className="venue-pb-value">
                            {best ? formatted.value : "—"}
                            {!!best && formatted.unit && (
                                <span className="venue-pb-unit">
                                    {" "}
                                    {formatted.unit}
                                </span>
                            )}
                        </div>
                        <div className="venue-pb-label">
                            {metricLabel(metric, t)}
                        </div>
                        <span className={badgeClass}>{t(KEY + badgeKey)}</span>
                    </>
                );

                // A tile with no best behind it — the metric was never tracked here — stays a
                // plain tile rather than a button that opens nothing.
                return matchId ? (
                    <button
                        type="button"
                        className="venue-pb venue-pb-tappable"
                        key={metric}
                        data-testid={`venue-pb-${metric}`}
                        onClick={() => onOpenMatch(matchId)}
                    >
                        {tile}
                    </button>
                ) : (
                    <div
                        className="venue-pb"
                        key={metric}
                        data-testid={`venue-pb-${metric}`}
                    >
                        {tile}
                    </div>
                );
            })}
        </div>
    );
};
